import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { numberToWords } from "../../../utils/numberToWords";
import { formatCurrency } from "../../../utils/formatCurrency";

export default function InvoiceTotals({ invoice, updateField, disabled = false }) {
  const [openTax, setOpenTax] = useState(false);
  const [openAdjustments, setOpenAdjustments] = useState(false);
  const [showCalculation, setShowCalculation] = useState(false);

  const items = invoice.items || [];
  const isSentOrPaid = invoice.status === "sent" || invoice.status === "paid";

  // For sent/paid invoices, use backend snapshot (immutable)
  // For draft invoices, calculate on the fly
  let subtotal, cgst, sgst, igst, taxTotal;

  if (isSentOrPaid && items.length > 0 && items[0].cgst_amount !== undefined) {
    // Use stored backend values (immutable snapshot)
    subtotal = items.reduce((sum, item) => {
      const base = (item.quantity || 0) * (item.rate || 0);
      const itemDiscount =
        item.discount_type === "percentage"
          ? (base * (item.discount || 0)) / 100
          : item.discount || 0;
      return sum + Math.max(0, base - itemDiscount);
    }, 0);

    cgst = items.reduce((sum, item) => sum + (Number(item.cgst_amount) || 0), 0);
    sgst = items.reduce((sum, item) => sum + (Number(item.sgst_amount) || 0), 0);
    igst = items.reduce((sum, item) => sum + (Number(item.igst_amount) || 0), 0);
    taxTotal = items.reduce((sum, item) => sum + (Number(item.tax) || 0), 0);
  } else {
    // Calculate on the fly for draft invoices
    subtotal = items.reduce((sum, item) => {
      const base = (item.quantity || 0) * (item.rate || 0);
      const discount =
        item.discountType === "percentage"
          ? (base * (item.discount || 0)) / 100
          : item.discount || 0;
      return sum + Math.max(0, base - discount);
    }, 0);

    const clientState = invoice.client?.state || "Maharashtra";
    const sameState = clientState === "Maharashtra";

    cgst = 0;
    sgst = 0;
    igst = 0;

    items.forEach((item) => {
      const amount = Math.max(
        0,
        (item.quantity || 0) * (item.rate || 0) -
          ((item.discountType === "percentage"
            ? ((item.quantity || 0) *
                (item.rate || 0) *
                (item.discount || 0)) /
              100
            : item.discount) || 0)
      );

      const tax = (amount * (item.gstRate || item.tax || 0)) / 100;

      if (sameState) {
        cgst += tax / 2;
        sgst += tax / 2;
      } else {
        igst += tax;
      }
    });

    taxTotal = cgst + sgst + igst;
  }

  const clientState = invoice.client?.state || "Maharashtra";
  const sameState = clientState === "Maharashtra";

  // Calculate discount based on type
  const discountType = invoice.discountType || "flat";
  const discountValue = invoice.discount || 0;
  const discountAmount =
    discountType === "percentage"
      ? (subtotal * discountValue) / 100
      : discountValue;

  const shipping = invoice.shipping || 0;
  const rounding = invoice.roundOff || 0;

  const total = subtotal + taxTotal - discountAmount + shipping + rounding;
  const balanceDue = total - (invoice.paidAmount || 0);

  return (
    <section className="space-y-3.5">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-900 tracking-tight">Totals</h2>
        {/* Trust signal */}
        <button 
          onClick={() => setShowCalculation(!showCalculation)}
          className="text-xs text-slate-500 hover:text-indigo-600 flex items-center gap-1 transition-all duration-150 hover:bg-indigo-50 px-2 py-1 rounded-md -mr-2"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          How is this calculated?
        </button>
      </div>

      {/* Calculation Explanation - Collapsible */}
      {showCalculation && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 space-y-2 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="flex items-start gap-2">
            <svg className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1 space-y-2">
              <h3 className="text-sm font-semibold text-indigo-900">Invoice Calculation</h3>
              <div className="text-xs text-indigo-800 space-y-1.5">
                <p><strong>Subtotal:</strong> Sum of all line items (Quantity × Rate - Discount)</p>
                <p><strong>GST:</strong> Calculated based on item GST rates</p>
                <ul className="ml-4 space-y-0.5 list-disc">
                  <li><strong>Same State:</strong> CGST + SGST (split equally)</li>
                  <li><strong>Inter-State:</strong> IGST (full rate)</li>
                </ul>
                <p><strong>Adjustments:</strong> Additional discounts, shipping, or rounding</p>
                <p><strong>Total:</strong> Subtotal + GST + Adjustments</p>
                <p><strong>Balance Due:</strong> Total - Payments Received</p>
              </div>
              <button
                onClick={() => setShowCalculation(false)}
                className="text-xs font-medium text-indigo-600 hover:text-indigo-700 hover:underline"
              >
                Got it, close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200/60 rounded-xl px-6 py-5 space-y-3 shadow-sm hover:shadow-lg hover:-translate-y-[1px] transition-all duration-200">
        {/* Subtotal */}
        <Row label="Subtotal" value={subtotal} />

        {/* GST Breakdown - Always visible */}
        {taxTotal > 0 && (
          <div className="space-y-2 py-3 border-y border-slate-100">
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
              GST Breakdown
            </div>
            <div className="space-y-1.5">
              {sameState ? (
                <>
                  <TaxBreakdownRow label="CGST" value={cgst} rate={items[0]?.gstRate / 2 || 0} />
                  <TaxBreakdownRow label="SGST" value={sgst} rate={items[0]?.gstRate / 2 || 0} />
                </>
              ) : (
                <TaxBreakdownRow label="IGST" value={igst} rate={items[0]?.gstRate || 0} />
              )}
              <div className="flex justify-between text-sm font-medium pt-1.5 border-t border-slate-100">
                <span className="text-slate-700">Total Tax</span>
                <span className="text-slate-900 tabular-nums">{formatCurrency(taxTotal, { showSymbol: false })}</span>
              </div>
            </div>
            <div className="text-[10px] text-slate-500 italic">
              {sameState
                ? `Same state (${clientState}): CGST + SGST applied`
                : "Inter-state: IGST applied"}
            </div>
          </div>
        )}

        {/* Adjustments - Collapsible */}
        <details className="group">
          <summary className="flex items-center justify-between cursor-pointer list-none hover:text-slate-900 transition-all duration-150 hover:bg-slate-50/50 -mx-2 px-2 py-1.5 rounded-md">
            <span className="text-sm text-slate-600 flex items-center gap-2">
              <ChevronDown size={14} className="transition-transform duration-200 group-open:rotate-180" />
              Adjustments
            </span>
            <span className="text-sm font-medium text-slate-700 tabular-nums">
              {formatCurrency(discountAmount + shipping + rounding, { showSymbol: false })}
            </span>
          </summary>
          <div className="mt-3 pl-6 space-y-3">
            {/* Discount with type toggle */}
            <DiscountField
              label="Discount"
              value={discountValue}
              type={discountType}
              subtotal={subtotal}
              onChange={(v) => !disabled && updateField("discount", v)}
              onTypeChange={(t) => {
                if (disabled) return;
                updateField("discountType", t);
                updateField("discount", 0);
              }}
              disabled={disabled}
            />
            <InlineField
              label="Shipping"
              value={invoice.shipping || 0}
              onChange={(v) => !disabled && updateField("shipping", v)}
              disabled={disabled}
            />
            <InlineField
              label="Rounding"
              value={invoice.roundOff || 0}
              onChange={(v) => !disabled && updateField("roundOff", v)}
              disabled={disabled}
            />
          </div>
        </details>

        {/* Grand Total */}
        <div className="pt-4 border-t-2 border-slate-300 space-y-3">
          <div className="flex justify-between items-baseline">
            <span className="text-lg font-bold text-slate-900">Total</span>
            <span className="text-3xl font-bold text-slate-900 tabular-nums tracking-tight">
              {formatCurrency(total)}
            </span>
          </div>

          <div className="flex justify-between text-sm">
            <span className="text-slate-600 font-medium">Balance Due</span>
            <span className="font-bold text-slate-900 tabular-nums">
              {formatCurrency(balanceDue)}
            </span>
          </div>

          {/* Amount in words */}
          <div className="text-xs text-slate-500 italic pt-2 border-t border-slate-100">
            {numberToWords(Math.floor(total))} rupees only
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───────── helpers ───────── */

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      <span className="font-medium">{formatCurrency(value, { showSymbol: false })}</span>
    </div>
  );
}

function RowSmall({ label, value }) {
  return (
    <div className="flex justify-between">
      <span>{label}</span>
      <span>₹ {value.toFixed(2)}</span>
    </div>
  );
}

function TaxBreakdownRow({ label, value, rate }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-600">
        {label} {rate > 0 && `(${rate}%)`}
      </span>
      <span className="font-medium text-slate-700 tabular-nums">{formatCurrency(value, { showSymbol: false })}</span>
    </div>
  );
}

function InlineField({ label, value, onChange, disabled = false }) {
  return (
    <div className="flex justify-between items-center gap-3 text-sm">
      <span className="text-slate-600">{label}</span>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        disabled={disabled}
        className={`
          w-28 text-right
          bg-slate-50 border border-slate-200 rounded-md
          px-2 py-1 text-sm
          focus:outline-none focus:ring-2 focus:ring-slate-200
          ${disabled ? "opacity-50 cursor-not-allowed" : ""}
        `}
      />
    </div>
  );
}

/* ───────── Discount Field with Type Toggle (Stripe-style) ───────── */

function DiscountField({ label, value, type, subtotal, onChange, onTypeChange, disabled = false }) {
  const calculatedAmount =
    type === "percentage" ? (subtotal * value) / 100 : value;

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center gap-3 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-slate-600">{label}</span>
          {/* Type Toggle */}
          <div className="inline-flex rounded-md p-0.5 bg-slate-100">
            <button
              onClick={() => !disabled && onTypeChange("flat")}
              disabled={disabled}
              className={`px-2 py-0.5 text-xs font-medium rounded transition ${
                type === "flat"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-600"
              } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              ₹
            </button>
            <button
              onClick={() => !disabled && onTypeChange("percentage")}
              disabled={disabled}
              className={`px-2 py-0.5 text-xs font-medium rounded transition ${
                type === "percentage"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-600"
              } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              %
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={value || ""}
            onChange={(e) => onChange(Number(e.target.value) || 0)}
            disabled={disabled}
            min={0}
            max={type === "percentage" ? 100 : undefined}
            className={`
              w-24 text-right
              bg-slate-50 border border-slate-200 rounded-md
              px-2 py-1 text-sm
              focus:outline-none focus:ring-2 focus:ring-slate-200
              ${disabled ? "opacity-50 cursor-not-allowed" : ""}
            `}
            placeholder="0"
          />
          {type === "percentage" && <span className="text-xs text-slate-500">%</span>}
        </div>
      </div>
      {value > 0 && (
        <div className="text-xs text-slate-500 text-right">
          = {formatCurrency(calculatedAmount, { showSymbol: false })}
        </div>
      )}
    </div>
  );
}

/* numberToWords stays same */
